import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators';
import { HisBackendService } from './his-backend.service';

/** Behavior options for the retrieval of a userProfile value. */
type GetValueOptions = 'refreshNow' | 'watch' | 'cached';
/** UserProfile type contract. */
export interface UserProfile {
    [key: string]: string | null;
}

/**
 * userprofile REST service invocations.
 */
@Injectable({providedIn: 'root'})
export class HisUserProfileService {
    private readonly backendService = inject(HisBackendService);

    private readonly userProfileSubject = new BehaviorSubject<Readonly<UserProfile>>( {} );
    private readonly userProfile$ = this.userProfileSubject.asObservable();
    private preloadUserProfileObservable?: Observable<Readonly<UserProfile>>;

    /**
     * Loads the entire userprofile if it has not been loaded yet and stores it.
     * Returns the userprofile in a one-shot Observable that immediately delivers the stored data
     * to new subscribers.
     */
    preloadUserProfile(): Observable<Readonly<UserProfile>> {
        if ( Object.keys( this.userProfileSubject.value ).length > 0 ) {
            // Early return if userprofile has already been loaded
            return this.userProfile$.pipe(take(1));
        }

        if ( !this.preloadUserProfileObservable ) {
            // Fetch entire userprofile from backend only if not already in progress
            this.preloadUserProfileObservable = this.backendService.getData('api/v1/cs/sys/userprofile/').pipe( tap( {
                next: ( profile: UserProfile ) => {
                    this.nextProfile( profile );
                    // Reset the observable reference after the data is loaded
                    this.preloadUserProfileObservable = undefined;
                },
                error: ( err ) => {
                    console.warn( 'Could not preload userProfile:', err );
                    // Reset the observable reference on error
                    this.preloadUserProfileObservable = undefined;
                }
            } ) );
        }

        // Return the observable that is in progress or already completed
        return this.preloadUserProfileObservable;
    }

    /** Returns the live *immutable* userProfile cache that emits on every new value. */
    public getUserProfile(): Observable<Readonly<UserProfile>> {
        return this.userProfile$;
    }

    /** Retuns the current *immutable* cache snapshot of the userProfile. */
    public getCachedUserProfile(): Readonly<UserProfile> {
        return this.userProfileSubject.value;
    }

    /**
     * Retrieves a value from the cached userProfile as a one-time firing observable.
     * @deprecated retained for legacy compatibility; use `getValueForKey` (big **F**) instead
     */
    getValueforKey( key: string ): Observable<string | null> {
        return this.getValueForKey( key, 'cached' );
    }

    /**
     * Retrieves a value for the specified `key` from the userProfile.
     * 
     * Behavior depends on the optional `options` parameter:
     * - `'refreshNow'`: fetches a single value directly from the backend and updates the cache.
     * - `'watch'`: returns a long-lived observable that emits whenever the cached value for `key` changes.\
     * **Note:** Remember to handle subscriptions, as the returned observable is not self-closing.
     * - `'cached'` (default): returns a one-time observable of the currently cached value, or `null` if not present.
     * @param key the key to look for
     * @param options execution options to modify this functions behavior
     * @returns an observable with the requested value
     */
    public getValueForKey( key: string, options: GetValueOptions = 'cached' ): Observable<string | null> {
        if ( options === 'refreshNow' ) {
            return this.getBackendValueForKey( key );
        }
        if ( options === 'watch' ) {
            return this.getObservableValueForKey( key );
        }
        return of( this.getCachedValueForKey( key ) );
    }

    /** Synchronously returns a value straight from the cache. */
    public getCachedValueForKey( key: string ): string | null {
        return this.hasValueCached( key ) ? this.userProfileSubject.value[key] : null;
    }

    /** Retrieves a value directly from the backend, storing it in the cache when it arrives. */
    private getBackendValueForKey( key: string ): Observable<string | null> {
        return this.backendService.getData( `api/v1/cs/sys/userprofile/${key}/` ).pipe(
            map( value => value ?? null ),
            tap( value => this.updateCachedValue( key, value ) )
        );
    }

    /** Returns a live observable of the value for the specified `key`. When a new distinct value is set, it will be emitted. */
    private getObservableValueForKey( key: string ): Observable<string | null> {
        return this.userProfile$.pipe(
            map( profile =>
                profile[key] ?? null
            ),
            distinctUntilChanged()
        );
    }

    public setNewValue( key: string, value: string | null, defaultValue: string | null ): Observable<string> {
        const data = { key, value, defaultValue };
        // currently the return value is just "Value set in userprofile"
        return this.backendService.postData( 'api/v1/cs/sys/userprofile/', data ).pipe(
            tap( () => {
                this.updateCachedValue( key, value ?? null );
            })
        );
    }

    public delete( key: string ): Observable<string> {
        return this.setNewValue( key, null, null );
    }

    private hasValueCached( key: string ) {
        return this.userProfileSubject.value[key] !== undefined;
    }

    private updateCachedValue( key: string, value: string | null ) {
        const next = { ...this.userProfileSubject.value, [key]: value };
        this.nextProfile( next );
    }

    private nextProfile( profile: UserProfile ) {
        this.userProfileSubject.next( Object.freeze( { ...profile } ) );
    }
}
